import React from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { STAFF_WORK_AREAS } from '../../constants/staffWorkArea'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function normWorkArea(v) {
  const raw = String(v || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (!raw) return ''
  if (raw === 'back_office') return 'backoffice'
  if (raw === 'floor_back_office' || raw === 'floor_and_back_office') return 'both'
  return STAFF_WORK_AREAS.some((w) => w.value === raw) ? raw : null
}

/** Check one row against designations, venues and existing employee IDs. Returns { errs, payload }. */
function checkRow(r, { designations, venues, existingIds, seenIds }) {
  const errs = []
  if (!r.name) errs.push('Full name required')
  const empId = r.employee_id.trim()
  if (empId) {
    if (existingIds.has(empId.toLowerCase())) errs.push('Employee ID already exists')
    else if (seenIds.has(empId.toLowerCase())) errs.push('Duplicate employee ID in file')
  }
  if (r.email && !EMAIL_RE.test(r.email)) errs.push('Invalid email')
  const designation = designations.find((d) => d.toLowerCase() === String(r.designation || '').trim().toLowerCase())
  if (!r.designation) errs.push('Designation required')
  else if (!designation) errs.push(`Unknown designation "${r.designation}"`)
  const workArea = normWorkArea(r.work_area)
  if (workArea === null) errs.push(`Unknown work area "${r.work_area}"`)
  let venue = null
  if (r.venue) {
    const v = r.venue.trim().toLowerCase()
    venue = venues.find((x) => String(x.venue_id).toLowerCase() === v || String(x.name || '').toLowerCase() === v) || null
    if (!venue) errs.push(`Unknown venue "${r.venue}"`)
  }
  if (String(r.start_date || '').startsWith('invalid:')) errs.push('Start date not recognised')
  const payload = {
    employee_id: empId || undefined,
    name: r.name,
    email: r.email || undefined,
    role: designation || r.designation,
    work_area: workArea || undefined,
    level: venue ? 'venue' : 'group',
    venue_id: venue ? venue.venue_id : null,
    start_date: r.start_date && !r.start_date.startsWith('invalid:') ? r.start_date : undefined,
    active: r.active,
  }
  return { errs, payload, venueName: venue?.name || '' }
}

export default function EmployeeBulkUploadModal({ designations, venues, existingEmployeeIds, onClose, onDone }) {
  const [rows, setRows] = React.useState([])
  const [fileName, setFileName] = React.useState('')
  const [error, setError] = React.useState(null)
  const [note, setNote] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const [results, setResults] = React.useState(null)
  const [done, setDone] = React.useState(false)

  const existingIds = React.useMemo(
    () => new Set((existingEmployeeIds || []).filter(Boolean).map((v) => String(v).toLowerCase())),
    [existingEmployeeIds]
  )

  const checked = React.useMemo(() => {
    const seenIds = new Set()
    return rows.map((r) => {
      const c = checkRow(r, { designations: designations || [], venues: venues || [], existingIds, seenIds })
      if (r.employee_id) seenIds.add(r.employee_id.trim().toLowerCase())
      return { ...r, ...c }
    })
  }, [rows, designations, venues, existingIds])

  const isOk = (v) => v === 'ok'
  const ready = checked.filter((r) => r.errs.length === 0 && !isOk(results?.[r.row_index]))

  const downloadTemplate = async () => {
    try {
      setError(null)
      const res = await authFetch(getLocalApiUrl('/api/admin/employee-bulk/template'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = 'employee-bulk-upload-template.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e?.message || 'Failed to download template')
    }
  }

  const onFile = async (file) => {
    if (!file) return
    setBusy(true)
    setError(null)
    setNote(null)
    setResults(null)
    setDone(false)
    setFileName(file.name)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await authFetch(getLocalApiUrl('/api/admin/employee-bulk/parse'), { method: 'POST', body: fd })
      const j = await res.json().catch(() => ({}))
      if (!res.ok || !j.success) throw new Error(j.error || `HTTP ${res.status}`)
      setRows(j.data.rows || [])
      if (j.data.truncated) setNote(`Only the first ${j.data.max_rows} rows were read. Upload the rest in a second file.`)
    } catch (e) {
      setRows([])
      setError(e?.message || 'Could not read file')
    } finally {
      setBusy(false)
    }
  }

  const createAll = async () => {
    if (!ready.length) return
    if (!window.confirm(`Create ${ready.length} employee(s)? No logins are created.`)) return
    setBusy(true)
    setError(null)
    const out = { ...(results || {}) }
    for (const r of ready) {
      try {
        const res = await authFetch(getLocalApiUrl('/api/staff'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(r.payload),
        })
        const j = await res.json().catch(() => ({}))
        out[r.row_index] = !res.ok || !j.success ? (j.error || `HTTP ${res.status}`) : 'ok'
      } catch (e) {
        out[r.row_index] = e?.message || 'Request failed'
      }
      setResults({ ...out })
    }
    setBusy(false)
    setDone(true)
    onDone?.()
  }

  const okCount = Object.values(results || {}).filter(isOk).length
  const failCount = Object.values(results || {}).length - okCount

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded shadow w-full max-w-5xl flex flex-col max-h-[calc(100vh-2rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
          <div className="text-lg font-semibold">Bulk upload employees</div>
          <button className="btn-secondary !text-sm !px-3 !py-1.5" onClick={onClose} disabled={busy}>Close</button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          <p className="text-sm text-gray-600">
            Download the Excel template, fill in one employee per row (up to 200), then upload it. Rows are checked first and nothing is created until you confirm.
            This creates employee records only. Logins are created in User Management.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-secondary !text-sm !px-3 !py-1.5" onClick={downloadTemplate}>Download template</button>
            <input type="file" accept=".xlsx,.xls" disabled={busy} onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }} />
            {fileName && <span className="text-xs text-gray-500">{fileName}</span>}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          {note && <div className="text-sm text-amber-700">{note}</div>}

          {checked.length > 0 && (
            <>
              <div className="text-sm">
                {checked.length} row(s): <span className="text-emerald-700">{checked.filter((r) => !r.errs.length).length} ready</span>,{' '}
                <span className="text-red-700">{checked.filter((r) => r.errs.length).length} with errors</span>
                {results && <> · created {okCount}{failCount > 0 && <>, failed {failCount}</>}</>}
              </div>
              <div className="overflow-x-auto border rounded">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Row</th>
                      <th className="px-2 py-1 text-left">Employee ID</th>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left">Email</th>
                      <th className="px-2 py-1 text-left">Designation</th>
                      <th className="px-2 py-1 text-left">Venue</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {checked.map((r) => {
                      const res = results?.[r.row_index]
                      return (
                        <tr key={r.row_index} className={r.errs.length ? 'bg-red-50' : ''}>
                          <td className="px-2 py-1">{r.row_index}</td>
                          <td className="px-2 py-1 font-mono">{r.employee_id}</td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1">{r.email}</td>
                          <td className="px-2 py-1">{r.designation}</td>
                          <td className="px-2 py-1">{r.venueName || (r.venue ? r.venue : 'Group')}</td>
                          <td className="px-2 py-1">
                            {res ? (
                              <span className={isOk(res) ? 'text-emerald-700' : 'text-red-700'}>{isOk(res) ? 'Created' : res}</span>
                            ) : r.errs.length ? (
                              <span className="text-red-700">{r.errs.join('; ')}</span>
                            ) : (
                              <span className="text-gray-500">Ready</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2 shrink-0">
          <button className="btn-secondary !text-sm !px-3 !py-1.5" onClick={onClose} disabled={busy}>{done ? 'Done' : 'Cancel'}</button>
          <button className="btn-primary !text-sm !px-3 !py-1.5" onClick={createAll} disabled={busy || ready.length === 0}>
            {busy ? 'Working…' : `Create ${ready.length} employee${ready.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
